import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import maplibregl, { type Map as MapInstance } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import type { Feature, FeatureCollection, LineString, Point } from "geojson";

import { useBaseMapInstance } from "./useBaseMapInstance";
import { smoothFitBounds } from "./smoothFitBounds";
import {
  computePolygonCentroid,
  EARTH_METERS_PER_DEG_LAT,
  haversineMeters,
  metersPerDegreeLon,
  normalizeVector,
  offsetMetersFromPivot,
  offsetTowards,
  type LngLatTuple,
} from "./mapGeometry";
import { upsertGeoJsonSource, ensureLayer, setLayerVisibility } from "./mapSources";

const angleFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

const distanceFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export type LotVertexLike = {
  longitude: number;
  latitude: number;
  seq_no?: number;
  corner?: string | null;
  interior_angle_deg?: number | null;
};

export type LotSegmentLike = {
  seq_no: number;
  distance_m?: number | null;
  is_tie?: boolean;
};

export type LotVerticesMapOverlayVisibility = {
  angles?: boolean;
  lengths?: boolean;
  corners?: boolean;
  cornerMarkers?: boolean;
};

export type LotVerticesMapStyle = {
  fillColor?: string;
  fillOpacity?: number;
  outlineColor?: string;
  outlineWidth?: number;
  cornerMarkerColor?: string;
  cornerMarkerStroke?: string;
  angleTextColor?: string;
  angleTextHaloColor?: string;
  lengthTextColor?: string;
  lengthTextHaloColor?: string;
  cornerLabelColor?: string;
  cornerLabelHaloColor?: string;
};

export type LotVerticesMapStatus = {
  isMapReady: boolean;
  hasRenderedTiles: boolean;
};

type LotVerticesMapProps = {
  vertices: LotVertexLike[];
  segments?: LotSegmentLike[];
  segmentAngles?: Map<number, number>;
  overlayVisibility?: LotVerticesMapOverlayVisibility;
  mapStyle: string;
  mapRef?: MutableRefObject<MapInstance | null>;
  className?: string;
  fitPadding?: number;
  boundsMaxZoom?: number;
  idPrefix?: string;
  styleOverrides?: LotVerticesMapStyle;
  onStatusChange?: (status: LotVerticesMapStatus) => void;
};

type AngleProperties = { label: string; seq: number; angle: number };
type CornerLabelProperties = { label: string; seq: number };
type LengthProperties = { label: string; seq: number };

type GeometryCollections = {
  polygonSource: FeatureCollection;
  vertexSource: FeatureCollection<Point, CornerLabelProperties>;
  angleCollection: FeatureCollection<Point, AngleProperties>;
  cornerLabelCollection: FeatureCollection<Point, CornerLabelProperties>;
  lengthCollection: FeatureCollection<LineString, LengthProperties>;
  bounds: maplibregl.LngLatBounds;
};

const defaultOverlayVisibility: Required<LotVerticesMapOverlayVisibility> = {
  angles: false,
  lengths: true,
  corners: true,
  cornerMarkers: true,
};

const defaultStyleOptions: Required<LotVerticesMapStyle> = {
  fillColor: "#22d3ee",
  fillOpacity: 0.25,
  outlineColor: "#06b6d4",
  outlineWidth: 2.5,
  cornerMarkerColor: "#06b6d4",
  cornerMarkerStroke: "#0f172a",
  angleTextColor: "#fbbf24",
  angleTextHaloColor: "#0f172a",
  lengthTextColor: "#f8fafc",
  lengthTextHaloColor: "#0f172a",
  cornerLabelColor: "#f8fafc",
  cornerLabelHaloColor: "#0f172a",
};

const formatMeters = (distance?: number | null): string | null => {
  if (typeof distance !== "number" || !Number.isFinite(distance)) {
    return null;
  }
  return `${distanceFormatter.format(distance)} m`;
};

const buildSegmentAngleMap = (
  segments: LotSegmentLike[] | undefined,
  vertexAngleMap: Map<number, { angle: number; coord: LngLatTuple }>,
  orderedSeqs: number[],
): Map<number, number> => {
  const map = new Map<number, number>();
  if (!segments || orderedSeqs.length === 0) {
    return map;
  }
  const firstSeq = orderedSeqs[0];
  const lastSeq = orderedSeqs[orderedSeqs.length - 1] ?? firstSeq;
  segments.forEach((segment) => {
    if (segment.is_tie) {
      return;
    }
    const targetSeq = segment.seq_no + 1;
    let entry =
      vertexAngleMap.get(targetSeq) ??
      (typeof lastSeq === "number" && targetSeq > lastSeq
        ? vertexAngleMap.get(firstSeq)
        : undefined);
    if (!entry && targetSeq === 1) {
      entry = vertexAngleMap.get(firstSeq);
    }
    if (entry) {
      map.set(segment.seq_no, entry.angle);
    }
  });
  return map;
};

const buildGeometry = (
  vertices: LotVertexLike[],
  segments: LotSegmentLike[] | undefined,
  providedSegmentAngles?: Map<number, number>,
): GeometryCollections | null => {
  const normalizedVertices = vertices
    .map((vertex, index) => {
      if (
        !Number.isFinite(vertex.longitude) ||
        !Number.isFinite(vertex.latitude)
      ) {
        return null;
      }
      const seq = typeof vertex.seq_no === "number" && vertex.seq_no > 0 ? vertex.seq_no : index + 1;
      return {
        ...vertex,
        seq_no: seq,
      };
    })
    .filter((vertex): vertex is Required<LotVertexLike> => vertex !== null);

  if (normalizedVertices.length < 3) {
    return null;
  }

  const coordinates = normalizedVertices.map(
    (vertex) => [vertex.longitude, vertex.latitude] as LngLatTuple,
  );
  const closedCoordinates =
    coordinates.length > 0 &&
    (coordinates[0][0] !== coordinates[coordinates.length - 1][0] ||
      coordinates[0][1] !== coordinates[coordinates.length - 1][1])
      ? [...coordinates, coordinates[0]]
      : coordinates;

  const seqToCoord = normalizedVertices.reduce<Map<number, LngLatTuple>>(
    (acc, vertex) => {
      acc.set(vertex.seq_no, [vertex.longitude, vertex.latitude]);
      return acc;
    },
    new Map(),
  );

  const orderedSeqs = [...seqToCoord.keys()].sort((a, b) => a - b);
  const firstSeqNo = orderedSeqs[0];
  const lastSeqNo = orderedSeqs[orderedSeqs.length - 1];

  const baseLabels = normalizedVertices.map((vertex) => {
    const trimmed =
      vertex.corner && vertex.corner.trim() ? vertex.corner.trim() : undefined;
    if (!trimmed) {
      return null;
    }
    if (trimmed.toLowerCase() === "tie point") {
      return null;
    }
    return trimmed;
  });

  const labelCounts = baseLabels.reduce<Map<string, number>>((acc, label) => {
    if (label) {
      acc.set(label, (acc.get(label) ?? 0) + 1);
    }
    return acc;
  }, new Map());

  const polygonSource: FeatureCollection = {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [closedCoordinates],
        },
        properties: {},
      },
    ],
  };

  const vertexSource: FeatureCollection<Point, CornerLabelProperties> = {
    type: "FeatureCollection",
    features: normalizedVertices.map((vertex, index) => {
      const baseLabel = baseLabels[index];
      const label =
        !baseLabel || (labelCounts.get(baseLabel) ?? 0) > 1
          ? String(vertex.seq_no)
          : baseLabel;
      return {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [vertex.longitude, vertex.latitude],
        },
        properties: {
          label,
          seq: vertex.seq_no,
        },
      };
    }),
  };

  const centroid = computePolygonCentroid(coordinates) ?? coordinates[0];
  const lonScale = metersPerDegreeLon(centroid[1]);
  const planarVertices = coordinates.map(
    ([lng, lat]) =>
      [
        (lng - centroid[0]) * lonScale,
        (lat - centroid[1]) * EARTH_METERS_PER_DEG_LAT,
      ] as [number, number],
  );
  const signedArea =
    planarVertices.length >= 3
      ? planarVertices.reduce((acc, point, index) => {
          const next = planarVertices[(index + 1) % planarVertices.length];
          return acc + point[0] * next[1] - next[0] * point[1];
        }, 0)
      : 0;
  const orientationSign = signedArea >= 0 ? 1 : -1;

  const vertexAngleMap = normalizedVertices.reduce<
    Map<number, { angle: number; coord: LngLatTuple }>
  >((acc, vertex) => {
    if (
      vertex.seq_no >= 1 &&
      typeof vertex.interior_angle_deg === "number" &&
      Number.isFinite(vertex.interior_angle_deg)
    ) {
      acc.set(vertex.seq_no, {
        angle: vertex.interior_angle_deg,
        coord: [vertex.longitude, vertex.latitude],
      });
    }
    return acc;
  }, new Map());

  const segmentAngleMap =
    providedSegmentAngles ??
    buildSegmentAngleMap(segments, vertexAngleMap, orderedSeqs);

  const angleFeatures: Feature<Point, AngleProperties>[] =
    segments && typeof firstSeqNo === "number"
      ? segments
          .map((segment) => {
            if (segment.is_tie) {
              return null;
            }
            const angle = segmentAngleMap.get(segment.seq_no);
            if (typeof angle !== "number" || !Number.isFinite(angle)) {
              return null;
            }
            const targetSeq = segment.seq_no + 1;
            const entry =
              vertexAngleMap.get(targetSeq) ??
              (typeof lastSeqNo === "number" && targetSeq > lastSeqNo
                ? vertexAngleMap.get(firstSeqNo)
                : undefined);
            if (!entry) {
              return null;
            }
            const base = entry.coord;
            const target = centroid ?? base;
            const [lng, lat] = offsetTowards(base, target, {
              ratio: 0.25,
              min: 0.000005,
              max: 0.00006,
            });
            return {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: [lng, lat],
              },
              properties: {
                angle,
                label: `${angleFormatter.format(angle)}°`,
                seq: segment.seq_no,
              },
            } satisfies Feature<Point, AngleProperties>;
          })
          .filter(
            (feature): feature is Feature<Point, AngleProperties> =>
              feature !== null,
          )
      : [];
  const angleCollection: FeatureCollection<Point, AngleProperties> = {
    type: "FeatureCollection",
    features: angleFeatures,
  };

  const cornerLabelOffsetMeters = 5;
  const cornerLabelFeatures: Feature<Point, CornerLabelProperties>[] =
    normalizedVertices
      .map((vertex, index) => {
        const baseLabel = baseLabels[index];
        const label =
          !baseLabel || (labelCounts.get(baseLabel) ?? 0) > 1
            ? String(vertex.seq_no)
            : baseLabel;
        if (!label) {
          return null;
        }
        const baseCoord: LngLatTuple = [vertex.longitude, vertex.latitude];
        let lng = baseCoord[0];
        let lat = baseCoord[1];
        if (planarVertices.length === normalizedVertices.length) {
          const current = planarVertices[index];
          const prevIndex =
            (index - 1 + planarVertices.length) % planarVertices.length;
          const nextIndex = (index + 1) % planarVertices.length;
          const prevVector: [number, number] = [
            current[0] - planarVertices[prevIndex][0],
            current[1] - planarVertices[prevIndex][1],
          ];
          const nextVector: [number, number] = [
            planarVertices[nextIndex][0] - current[0],
            planarVertices[nextIndex][1] - current[1],
          ];
          const normalizedPrev = normalizeVector(prevVector);
          const normalizedNext = normalizeVector(nextVector);
          const outwardPrev: [number, number] =
            orientationSign >= 0
              ? [normalizedPrev[1], -normalizedPrev[0]]
              : [-normalizedPrev[1], normalizedPrev[0]];
          const outwardNext: [number, number] =
            orientationSign >= 0
              ? [normalizedNext[1], -normalizedNext[0]]
              : [-normalizedNext[1], normalizedNext[0]];
          let outward = normalizeVector([
            outwardPrev[0] + outwardNext[0],
            outwardPrev[1] + outwardNext[1],
          ]);
          if (outward[0] === 0 && outward[1] === 0) {
            outward = normalizeVector(outwardPrev);
          }
          if (outward[0] !== 0 || outward[1] !== 0) {
            lng += (outward[0] * cornerLabelOffsetMeters) / lonScale;
            lat +=
              (outward[1] * cornerLabelOffsetMeters) / EARTH_METERS_PER_DEG_LAT;
          } else {
            const fallback = offsetMetersFromPivot(
              baseCoord,
              centroid,
              cornerLabelOffsetMeters,
            );
            lng = fallback[0];
            lat = fallback[1];
          }
        } else {
          const fallback = offsetMetersFromPivot(
            baseCoord,
            centroid,
            cornerLabelOffsetMeters,
          );
          lng = fallback[0];
          lat = fallback[1];
        }
        return {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [lng, lat],
          },
          properties: {
            label,
            seq: vertex.seq_no,
          },
        } satisfies Feature<Point, CornerLabelProperties>;
      })
      .filter(
        (feature): feature is Feature<Point, CornerLabelProperties> =>
          feature !== null,
      );
  const cornerLabelCollection: FeatureCollection<Point, CornerLabelProperties> =
    {
      type: "FeatureCollection",
      features: cornerLabelFeatures,
    };

  const lengthFeatures: Feature<LineString, LengthProperties>[] =
    segments && typeof firstSeqNo === "number"
      ? segments.reduce<Feature<LineString, LengthProperties>[]>(
          (acc, segment) => {
            if (segment.is_tie) {
              return acc;
            }
            const start = seqToCoord.get(segment.seq_no);
            const nextSeq =
              segment.seq_no + 1 > (lastSeqNo ?? segment.seq_no)
                ? firstSeqNo
                : segment.seq_no + 1;
            const end = seqToCoord.get(nextSeq);
            const fallbackLength =
              segment && Number.isFinite(segment.distance_m)
                ? segment.distance_m
                : null;
            const computedLength =
              start && end
                ? haversineMeters([start[0], start[1]], [end[0], end[1]])
                : null;
            const labelValue =
              fallbackLength && Number.isFinite(fallbackLength)
                ? fallbackLength
                : computedLength;
            const label = formatMeters(labelValue);
            if (
              !start ||
              !end ||
              !label ||
              !Number.isFinite(start[0]) ||
              !Number.isFinite(start[1]) ||
              !Number.isFinite(end[0]) ||
              !Number.isFinite(end[1])
            ) {
              return acc;
            }
            const startCoord: [number, number] = [start[0], start[1]];
            const endCoord: [number, number] = [end[0], end[1]];
            acc.push({
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: [startCoord, endCoord],
              },
              properties: {
                label,
                seq: segment.seq_no,
              },
            });
            return acc;
          },
          [],
        )
      : [];
  const lengthCollection: FeatureCollection<LineString, LengthProperties> = {
    type: "FeatureCollection",
    features: lengthFeatures,
  };

  const bounds = coordinates.reduce(
    (acc, coord) => acc.extend(coord as [number, number]),
    new maplibregl.LngLatBounds(coordinates[0], coordinates[0]),
  );

  return {
    polygonSource,
    vertexSource,
    angleCollection,
    cornerLabelCollection,
    lengthCollection,
    bounds,
  };
};

export function LotVerticesMap({
  vertices,
  segments,
  segmentAngles,
  overlayVisibility,
  mapStyle,
  mapRef,
  className,
  fitPadding = 48,
  boundsMaxZoom = 19,
  idPrefix = "lot-geometry",
  styleOverrides,
  onStatusChange,
}: LotVerticesMapProps) {
  const resolvedOverlay = useMemo(
    () => ({ ...defaultOverlayVisibility, ...overlayVisibility }),
    [overlayVisibility],
  );
  const styleOptions = useMemo(
    () => ({ ...defaultStyleOptions, ...styleOverrides }),
    [styleOverrides],
  );

  const geometry = useMemo(
    () => buildGeometry(vertices, segments, segmentAngles),
    [vertices, segments, segmentAngles],
  );

  const hasFittedBoundsRef = useRef(false);

  useEffect(() => {
    hasFittedBoundsRef.current = false;
  }, [geometry, segments?.length, vertices.length]);

  const maybeFitToGeometry = useCallback(
    (map: MapInstance) => {
      if (!geometry) {
        return;
      }
      if (hasFittedBoundsRef.current) {
        return;
      }
      smoothFitBounds(map, geometry.bounds, {
        padding: fitPadding,
        maxZoom: boundsMaxZoom,
        durationMs: 1100,
      });
      hasFittedBoundsRef.current = true;
    },
    [boundsMaxZoom, fitPadding, geometry],
  );

  const refreshMap = useCallback(
    (instance: MapInstance) => {
      if (!geometry) {
        return;
      }
      const polygonSourceId = `${idPrefix}-polygon`;
      const vertexSourceId = `${idPrefix}-vertices`;
      const cornerCircleLayerId = `${idPrefix}-vertices-circle`;
      const angleSourceId = `${idPrefix}-angle-labels`;
      const angleLayerId = `${idPrefix}-angle-label`;
      const cornerLabelSourceId = `${idPrefix}-vertex-labels`;
      const cornerLabelLayerId = `${idPrefix}-vertex-label`;
      const lengthSourceId = `${idPrefix}-segment-lengths`;
      const lengthLayerId = `${idPrefix}-segment-length-labels`;

      upsertGeoJsonSource(instance, polygonSourceId, {
        type: "geojson",
        data: geometry.polygonSource,
      });
      upsertGeoJsonSource(instance, vertexSourceId, {
        type: "geojson",
        data: geometry.vertexSource,
      });

      ensureLayer(instance, {
        id: `${idPrefix}-polygon-fill`,
        type: "fill",
        source: polygonSourceId,
        paint: {
          "fill-color": styleOptions.fillColor,
          "fill-opacity": styleOptions.fillOpacity,
        },
      });

      ensureLayer(instance, {
        id: `${idPrefix}-polygon-outline`,
        type: "line",
        source: polygonSourceId,
        paint: {
          "line-color": styleOptions.outlineColor,
          "line-width": styleOptions.outlineWidth,
        },
      });

      const cornerCircleVisibility = resolvedOverlay.cornerMarkers
        ? "visible"
        : "none";
      ensureLayer(instance, {
        id: cornerCircleLayerId,
        type: "circle",
        source: vertexSourceId,
        layout: {
          visibility: cornerCircleVisibility,
        },
        paint: {
          "circle-radius": 5,
          "circle-color": styleOptions.cornerMarkerColor,
          "circle-stroke-width": 1,
          "circle-stroke-color": styleOptions.cornerMarkerStroke,
        },
      });
      setLayerVisibility(instance, cornerCircleLayerId, cornerCircleVisibility);

      if (geometry.angleCollection.features.length > 0) {
        upsertGeoJsonSource(instance, angleSourceId, {
          type: "geojson",
          data: geometry.angleCollection,
        });
        const visibility = resolvedOverlay.angles ? "visible" : "none";
        ensureLayer(instance, {
          id: angleLayerId,
          type: "symbol",
          source: angleSourceId,
          layout: {
            "text-field": ["get", "label"],
            "text-size": 11,
            "text-anchor": "center",
            "text-offset": [0, 0.1],
            visibility,
          },
          paint: {
            "text-color": styleOptions.angleTextColor,
            "text-halo-color": styleOptions.angleTextHaloColor,
            "text-halo-width": 1.1,
          },
        });
        setLayerVisibility(instance, angleLayerId, visibility);
      } else {
        if (instance.getLayer(angleLayerId)) {
          instance.removeLayer(angleLayerId);
        }
        if (instance.getSource(angleSourceId)) {
          instance.removeSource(angleSourceId);
        }
      }

      if (geometry.cornerLabelCollection.features.length > 0) {
        upsertGeoJsonSource(instance, cornerLabelSourceId, {
          type: "geojson",
          data: geometry.cornerLabelCollection,
        });
        const visibility = resolvedOverlay.corners ? "visible" : "none";
        ensureLayer(instance, {
          id: cornerLabelLayerId,
          type: "symbol",
          source: cornerLabelSourceId,
          layout: {
            "text-field": ["get", "label"],
            "text-size": 12,
            "text-variable-anchor": [
              "top",
              "top-right",
              "right",
              "bottom-right",
              "bottom",
              "bottom-left",
              "left",
              "top-left",
            ],
            "text-radial-offset": 0.25,
            "text-allow-overlap": false,
            visibility,
          },
          paint: {
            "text-color": styleOptions.cornerLabelColor,
            "text-halo-color": styleOptions.cornerLabelHaloColor,
            "text-halo-width": 1,
          },
        });
        setLayerVisibility(instance, cornerLabelLayerId, visibility);
      } else {
        if (instance.getLayer(cornerLabelLayerId)) {
          instance.removeLayer(cornerLabelLayerId);
        }
        if (instance.getSource(cornerLabelSourceId)) {
          instance.removeSource(cornerLabelSourceId);
        }
      }

      if (geometry.lengthCollection.features.length > 0) {
        upsertGeoJsonSource(instance, lengthSourceId, {
          type: "geojson",
          data: geometry.lengthCollection,
        });
        const visibility = resolvedOverlay.lengths ? "visible" : "none";
        ensureLayer(instance, {
          id: lengthLayerId,
          type: "symbol",
          source: lengthSourceId,
          layout: {
            "symbol-placement": "line-center",
            "text-field": ["get", "label"],
            "text-size": 11,
            "text-keep-upright": true,
            visibility,
          },
          paint: {
            "text-color": styleOptions.lengthTextColor,
            "text-halo-color": styleOptions.lengthTextHaloColor,
            "text-halo-width": 1.1,
          },
        });
        setLayerVisibility(instance, lengthLayerId, visibility);
      } else {
        if (instance.getLayer(lengthLayerId)) {
          instance.removeLayer(lengthLayerId);
        }
        if (instance.getSource(lengthSourceId)) {
          instance.removeSource(lengthSourceId);
        }
      }

      maybeFitToGeometry(instance);
    },
    [
      geometry,
      idPrefix,
      maybeFitToGeometry,
      resolvedOverlay.angles,
      resolvedOverlay.cornerMarkers,
      resolvedOverlay.corners,
      resolvedOverlay.lengths,
      styleOptions.angleTextColor,
      styleOptions.angleTextHaloColor,
      styleOptions.cornerLabelColor,
      styleOptions.cornerLabelHaloColor,
      styleOptions.cornerMarkerColor,
      styleOptions.cornerMarkerStroke,
      styleOptions.fillColor,
      styleOptions.fillOpacity,
      styleOptions.lengthTextColor,
      styleOptions.lengthTextHaloColor,
      styleOptions.outlineColor,
      styleOptions.outlineWidth,
    ],
  );

  const center = useMemo(
    () => geometry?.bounds?.getCenter() ?? undefined,
    [geometry],
  );

  const mapOptions = useMemo(
    () => (center ? { center } : undefined),
    [center],
  );

  const {
    containerRef,
    mapRef: baseMapRef,
    status,
    resize,
  } = useBaseMapInstance({
    style: mapStyle,
    mapOptions,
    mapRefOverride: mapRef,
    onLoad: (map) => {
      if (geometry) {
        refreshMap(map);
      }
    },
    onStyleData: (map) => {
      if (geometry) {
        refreshMap(map);
      }
    },
  });

  const resolvedMapRef = mapRef ?? baseMapRef;

  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  useEffect(() => {
    const map = resolvedMapRef.current;
    if (!map || !geometry) {
      return;
    }
    const run = () => {
      if (resolvedMapRef.current === map && geometry) {
        refreshMap(map);
      }
    };
    if (!map.isStyleLoaded()) {
      map.once("styledata", run);
      return;
    }
    run();
  }, [geometry, refreshMap, resolvedMapRef]);

  useEffect(() => {
    resize();
  }, [resize]);

  useEffect(() => {
    const map = resolvedMapRef.current;
    if (!map) {
      return;
    }
    const cornerCircleLayerId = `${idPrefix}-vertices-circle`;
    if (map.getLayer(cornerCircleLayerId)) {
      map.setLayoutProperty(
        cornerCircleLayerId,
        "visibility",
        resolvedOverlay.cornerMarkers ? "visible" : "none",
      );
    }
    const cornerLabelLayerId = `${idPrefix}-vertex-label`;
    if (map.getLayer(cornerLabelLayerId)) {
      map.setLayoutProperty(
        cornerLabelLayerId,
        "visibility",
        resolvedOverlay.corners ? "visible" : "none",
      );
    }
    const angleLayerId = `${idPrefix}-angle-label`;
    if (map.getLayer(angleLayerId)) {
      map.setLayoutProperty(
        angleLayerId,
        "visibility",
        resolvedOverlay.angles ? "visible" : "none",
      );
    }
    const lengthLayerId = `${idPrefix}-segment-length-labels`;
    if (map.getLayer(lengthLayerId)) {
      map.setLayoutProperty(
        lengthLayerId,
        "visibility",
        resolvedOverlay.lengths ? "visible" : "none",
      );
    }
  }, [
    idPrefix,
    resolvedMapRef,
    resolvedOverlay.angles,
    resolvedOverlay.cornerMarkers,
    resolvedOverlay.corners,
    resolvedOverlay.lengths,
  ]);

  return <div ref={containerRef} className={className} role="presentation" />;
}
